/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IDataServiceRequest } from '@msdyn365-commerce/retail-proxy';
import { subscriptionCancelInput, subscriptionPauseInput, subscriptionResumeInput } from '../../../dataActions/DataActionExtension.g';
import { IrsmSubscriptionListResponse } from '../../../dataActions/DataServiceEntities.g';

/**
 * GetSubscriptions Input Action
 */
export class SubscriptionState implements Msdyn365.IActionInput {
    public input: IDataServiceRequest;
    constructor(input: IDataServiceRequest) {
        this.input = input;
    }

    public shouldCacheOutput = () => false;

    public getCacheKey = () => `IGetSubscriptionsInput`;
    public getCacheObjectType = () => 'IGetSubscriptionsInput';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface ISubscriptionStateData extends IrsmSubscriptionListResponse { }

export type state = 'pause' | 'resume' | 'cancel';

export interface IInputRequirements {
    customerId: string;
    subscriptionId: string;
    newState: state;
}

export const createSubscriptionStateInput = (args: Msdyn365.ICreateActionContext, input: IInputRequirements): SubscriptionState => {
    switch (input.newState) {
        case 'cancel':
            return new SubscriptionState(
                subscriptionCancelInput(input.subscriptionId, input.customerId)
            );
        case 'pause':
            return new SubscriptionState(
                subscriptionPauseInput(input.subscriptionId, input.customerId)
            );
        case 'resume':
            return new SubscriptionState(
                subscriptionResumeInput(input.subscriptionId, input.customerId)
            );
        default:
            // this can't happen but typescript really wants something returned.
            return new SubscriptionState(
                subscriptionResumeInput(input.subscriptionId, input.customerId)
            );
    }
};

export async function createSubscriptionStateAction(input: SubscriptionState, ctx: Msdyn365.IActionContext): Promise<ISubscriptionStateData> {
    return input.input.execute(ctx);
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ISubscriptionStateData>>createSubscriptionStateAction,
    input: createSubscriptionStateInput
});
